import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';

const IDQR = () => {
    const [qrCode, setQrCode] = useState(null);
    const [error, setError] = useState(null);

    // Convert PEM formatted public key to an ArrayBuffer
    const convertPemToArrayBuffer = (pem) => {
        try {
            console.log("Converting PEM to ArrayBuffer");
            const b64 = pem.replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\n|\r/g, '');
            const binary = window.atob(b64);
            const buffer = new ArrayBuffer(binary.length);
            const view = new Uint8Array(buffer);
            for (let i = 0; i < binary.length; i++) {
                view[i] = binary.charCodeAt(i);
            }
            console.log("PEM converted successfully");
            return buffer;
        } catch (error) {
            console.error('Error converting PEM to ArrayBuffer: ', error);
            throw error;
        }
    };

    // Import the RSA public key for RSA-OAEP encryption
    const importPublicKey = async (pem) => {
        const keyBuffer = convertPemToArrayBuffer(pem);
        try {
            console.log("Importing RSA public key");
            return await window.crypto.subtle.importKey(
                'spki',                               // Format of the public key
                keyBuffer,                            // Key data as ArrayBuffer
                { name: 'RSA-OAEP', hash: 'SHA-256' },// Algorithm and hash function
                true,                                 // Key is extractable
                ['encrypt']                           // Key usage
            );
        } catch (error) {
            console.error('Error importing public key: ', error);
            throw error;
        }
    };

    // Generate an AES-GCM key
    const generateAESKey = async () => {
        console.log("Generating AES key");
        return await window.crypto.subtle.generateKey(
            { name: "AES-GCM", length: 256 }, // AES-GCM with 256-bit key
            true,                             // Extractable
            ["encrypt", "decrypt"]
        );
    };

    // Encrypt data with the AES key
    const encryptAES = async (aesKey, token) => {
        const enc = new TextEncoder();
        const encoded = enc.encode(token);  // JWT is already a string
        const iv = window.crypto.getRandomValues(new Uint8Array(12));  // Generate IV for AES-GCM
        
        console.log("IV (Base64):", btoa(String.fromCharCode(...iv)));  // Log the base64 IV for reference
    
        const encrypted = await window.crypto.subtle.encrypt(
            { name: "AES-GCM", iv: iv },  // AES-GCM with IV (Uint8Array, not base64)
            aesKey,
            encoded
        );
    
        // Log the encrypted data length (should include the authentication tag)
        console.log("Encrypted data length (with tag):", new Uint8Array(encrypted).length);
    
        return { 
            aesEncryptedData: btoa(String.fromCharCode(...new Uint8Array(encrypted))),  // AES-encrypted JWT (Base64)
            iv: btoa(String.fromCharCode(...iv))  // Base64 encode IV to send to the server
        };
    };
    
    // Encrypt the AES key using the RSA public key
    const encryptAESKeyWithRSA = async (publicKey, aesKey) => {
        console.log("Encrypting AES key with RSA public key");
        const exportedKey = await window.crypto.subtle.exportKey("raw", aesKey);  // Export raw AES key
        const encryptedAESKey = await window.crypto.subtle.encrypt(
            {
                name: "RSA-OAEP",  // Use RSA-OAEP algorithm
                hash: { name: "SHA-256" }  // Hash function for OAEP padding
            },
            publicKey,  // Public RSA key
            exportedKey  // AES key to encrypt
        );

        console.log("AES key encrypted with RSA");
        return encryptedAESKey;
    };

    // Generate the QR Code
    const generateQRCode = async () => {
        try {
            const token = sessionStorage.getItem('token');
            if (!token) throw new Error('User token is missing in sessionStorage');

            console.log("JWT Token from sessionStorage:", token);

            const publicKeyPEM = process.env.REACT_APP_PUBLIC_KEY;
            if (!publicKeyPEM) throw new Error("Public key is missing in environment variables");

            console.log("Public Key PEM:", publicKeyPEM);

            // Import the RSA public key
            const publicKey = await importPublicKey(publicKeyPEM);

            // Generate an AES key
            const aesKey = await generateAESKey();

            // Encrypt the token using AES
            const { aesEncryptedData, iv } = await encryptAES(aesKey, token);

            console.log("AES Encrypted Data:", aesEncryptedData);
            console.log("IV used for encryption:", iv);

            // Encrypt the AES key using RSA
            const encryptedAESKey = await encryptAESKeyWithRSA(publicKey, aesKey);

            console.log("RSA Encrypted AES Key:", encryptedAESKey);

            // Combine the AES-encrypted data, the AES IV, and the RSA-encrypted AES key
            const combinedData = {
                aesEncryptedData, // AES-encrypted data
                iv,               // AES IV
                encryptedAESKey: btoa(String.fromCharCode(...new Uint8Array(encryptedAESKey))) // RSA-encrypted AES key (Base64)
            };

            console.log("Combined data for QR:", combinedData);

            // Generate the QR code from the combined data
            const qrData = JSON.stringify(combinedData);
            QRCode.toDataURL(qrData, (err, url) => {
                if (err) {
                    console.error('Error generating QR code:', err);
                    return;
                }
                setQrCode(url); // Set the QR code URL to display it
            });

        } catch (error) {
            console.error('Error during QR code generation:', error);
            setError(error.message);
        }
    };

    // Ensure QR code generation only happens once on component mount
    useEffect(() => {
        generateQRCode();  // This will only run once due to the empty dependency array
    }, []);

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            {error && <p>{error}</p>}
            {qrCode ? (
                <img src={qrCode} alt="QR Code" />
            ) : (
                <p>Generating QR code...</p>
            )}
        </div>
    );
};

export default IDQR;
